import { useState, useEffect, useCallback, useMemo } from 'react';
import { aiAgentsAPI } from '../../services/api';
import toast from '../../services/toast';
import { getThreadDisplayTitle } from './threadDisplayTitle';

/**
 * Thread list, selection, and deletion for Vanti chat history.
 */
export function useAIChatThreads({
  open: _open,
  alwaysLoad = false,
  selectedAgentId,
  agents,
  setSelectedAgentId: _setSelectedAgentId,
  setMessages,
  setFeedbackState,
  messages,
}: any) {
  const [threadId, setThreadId] = useState(() => localStorage.getItem('vanti.threadId') || '');
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [threads, setThreads] = useState<any[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [currentThreadTitle, setCurrentThreadTitle] = useState('');

  const loadThreads = useCallback(async () => {
    setThreadsLoading(true);
    try {
      const r = await aiAgentsAPI.listThreads({
        scope: 'all',
        agentId: selectedAgentId || undefined,
      });
      setThreads(r.threads || []);
    } catch {
      setThreads([]);
    } finally {
      setThreadsLoading(false);
    }
  }, [selectedAgentId]);

  useEffect(() => {
    if (threadsOpen || alwaysLoad) loadThreads();
  }, [threadsOpen, alwaysLoad, loadThreads]);

  // Auto-restore messages when returning to the page with a saved threadId but empty messages state.
  const messagesLength = Array.isArray(messages) ? messages.length : 0;
  useEffect(() => {
    if (threadId && messagesLength === 0) {
      handleSelectThread(threadId);
    }
    // Only run on mount — intentionally omitting handleSelectThread from deps to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeThreadForTitle = useMemo(() => {
    if (!threadId) return null;
    return threads.find((t) => t.id === threadId) ?? { title: currentThreadTitle };
  }, [threadId, threads, currentThreadTitle]);

  const activeThreadDisplayTitle = activeThreadForTitle
    ? getThreadDisplayTitle(activeThreadForTitle)
    : '';

  const handleSelectThread = async (id) => {
    setThreadsOpen(false);
    setThreadId(id);
    localStorage.setItem('vanti.threadId', id);
    setFeedbackState({});
    const fromList = threads.find((t) => t.id === id);
    setCurrentThreadTitle(getThreadDisplayTitle(fromList));
    try {
      const r = await aiAgentsAPI.getThread(id);
      const msgs = (r.thread?.messages || []).map((m) => {
        const meta = m.metaJson && typeof m.metaJson === 'object' ? (m.metaJson as any) : {};
        return {
          role: m.role,
          content: m.content,
          id: m.id,
          turnId: m.role === 'assistant' ? m.id : undefined,
          ...(m.role === 'assistant' ? {
            charts: Array.isArray(meta.charts) ? meta.charts : [],
            vegaCharts: Array.isArray(meta.vegaCharts) ? meta.vegaCharts : [],
            tables: Array.isArray(meta.tables) ? meta.tables : [],
            summaries: Array.isArray(meta.summaries) ? meta.summaries : [],
            questions: Array.isArray(meta.questions) ? meta.questions : [],
            quickActions: Array.isArray(meta.quickActions) ? meta.quickActions : [],
            ragSources: Array.isArray(meta.ragSources) ? meta.ragSources : [],
            memoryUsed: Boolean(meta.memoryUsed),
          } : {}),
        };
      });

      const updatedAt = r.thread?.updatedAt ? new Date(r.thread.updatedAt) : null;
      const hoursSince = updatedAt ? (Date.now() - updatedAt.getTime()) / 1000 / 3600 : null;
      if (hoursSince !== null && hoursSince > 4) {
        const timeLabel =
          hoursSince < 24
            ? `${Math.floor(hoursSince)} hours ago`
            : hoursSince < 48
              ? 'Yesterday'
              : `${Math.floor(hoursSince / 24)} days ago`;
        msgs.unshift({
          id: `resumed-${Date.now()}`,
          type: 'conversation_resumed',
          timeLabel,
        });
      }

      const threadAgentId = r.thread?.agentId || fromList?.agentId || '';
      const threadAgentName = threadAgentId
        ? agents.find((a) => a.id === threadAgentId)?.name || 'Custom Agent'
        : 'Vanti (built-in)';
      if (threadAgentId !== selectedAgentId) {
        msgs.push({ id: `agent-${Date.now()}`, type: 'agent_change', agentLabel: threadAgentName });
      }

      setMessages(msgs);
      const firstUser = (r.thread?.messages || []).find((m) => m.role === 'user');
      setCurrentThreadTitle(
        getThreadDisplayTitle({
          title: r.thread?.title ?? fromList?.title,
          firstMessage: firstUser?.content ?? fromList?.firstMessage,
        })
      );
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === 'NOT_FOUND') {
        // Stale localStorage / deleted thread — drop the id so we stop refetching on every visit.
        setThreadId('');
        localStorage.removeItem('vanti.threadId');
        setCurrentThreadTitle('');
        setMessages([]);
        return;
      }
      toast.error((err as Error).message || 'Failed to load conversation');
    }
  };

  const handleDeleteThread = async (id) => {
    try {
      await aiAgentsAPI.deleteThread(id);
      if (threadId === id) {
        setMessages([]);
        setThreadId('');
        localStorage.removeItem('vanti.threadId');
        setFeedbackState({});
        setCurrentThreadTitle('');
      }
      loadThreads();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to delete conversation');
    }
  };

  const clearThreadState = () => {
    setThreadId('');
    localStorage.removeItem('vanti.threadId');
    setCurrentThreadTitle('');
  };

  return {
    threadId,
    setThreadId,
    threadsOpen,
    setThreadsOpen,
    threads,
    setThreads,
    threadsLoading,
    currentThreadTitle,
    setCurrentThreadTitle,
    loadThreads,
    activeThreadDisplayTitle,
    handleSelectThread,
    handleDeleteThread,
    clearThreadState,
  };
}
